import React from "react";
import { Plus, Trash2, Trophy, Users, Calendar, Award } from "lucide-react";

/** Default rating systems by sport — sets a sensible default placeholder
 *  rather than locking the user into one scheme. They can override the
 *  `skill_rating_system` on each class.  */
const DEFAULT_RATING_BY_SPORT = {
  pickleball: "DUPR",
  tennis: "UTR",
  padel: "WPT",
  chess: "FIDE",
  badminton: "BWF",
};

const GENDERS  = [{v: "mixed", l: "Mixed"}, {v: "men", l: "Men"}, {v: "women", l: "Women"}];
const FORMATS  = [
  {v: "doubles", l: "Doubles"},
  {v: "singles", l: "Singles"},
  {v: "mixed_doubles", l: "Mixed doubles"},
  {v: "teams", l: "Teams"},
];

const newClass = (sportSlug) => ({
  id: undefined,  // server stamps
  title: "",
  gender: "mixed",
  format: "doubles",
  age_min: null,
  age_max: null,
  skill_rating_system: DEFAULT_RATING_BY_SPORT[sportSlug] || "",
  skill_rating_min: null,
  skill_rating_max: null,
  capacity: 16,
  price: 0,
  currency: "EUR",
  notes: "",
});

export { newClass };

/**
 * Tournament classes editor — one tournament holds many classes (categories).
 * Each class captures its own gender, format, age band, skill rating filter,
 * capacity, and entry price. Players register per-class on the detail page.
 *
 * Props:
 *   classes: list of class objects
 *   onChange(classes)
 *   sportSlug: used for rating-system default (DUPR/UTR/etc.)
 */
export default function TournamentClassesEditor({ classes = [], onChange, sportSlug, sports = [], primarySportId }) {
  const add = () => onChange([...(classes || []), newClass(sportSlug)]);
  // Always keep at least one class — a tournament with zero classes can't take registrations.
  const drop = (idx) => {
    const next = (classes || []).filter((_, i) => i !== idx);
    onChange(next.length === 0 ? [newClass(sportSlug)] : next);
  };
  const update = (idx, patch) => onChange(classes.map((c, i) => i === idx ? { ...c, ...patch } : c));

  return (
    <div className="space-y-3" data-testid="tournament-classes-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy size={14} className="text-[#FFD60A]" />
          <h4 className="font-heading text-sm font-bold uppercase tracking-wider">
            Tournament classes
          </h4>
          <span className="text-[10px] text-zinc-500">
            ({classes.length || 0})
          </span>
        </div>
        <button
          type="button"
          onClick={add}
          data-testid="tour-class-add"
          className="pp-btn-secondary px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"
        >
          <Plus size={11} /> Add class
        </button>
      </div>
      <p className="text-[11px] text-zinc-500">
        One tournament can host many classes (e.g. "Mixed Doubles Open", "Men 50+ Singles").
        Each class has its own capacity, price, and skill-rating filter — players register per-class.
      </p>

      {(classes || []).map((cl, idx) => (
        <div
          key={cl.id || idx}
          data-testid={`tour-class-${idx}`}
          className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3"
        >
          <div className="flex items-center justify-between gap-2">
            <input
              type="text"
              value={cl.title}
              onChange={(e) => update(idx, { title: e.target.value })}
              placeholder="e.g. Mixed Doubles Open"
              data-testid={`tour-class-${idx}-title`}
              className="pp-input flex-1 font-medium"
            />
            <button
              type="button"
              onClick={() => drop(idx)}
              data-testid={`tour-class-${idx}-drop`}
              className="p-1.5 rounded-md border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"
              title="Remove class"
            >
              <Trash2 size={11} />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sports.length > 1 && (
              <Field label="Sport" icon={<Trophy size={10} />}>
                <select
                  value={cl.sport_id || ""}
                  onChange={(e) => update(idx, { sport_id: e.target.value || null })}
                  data-testid={`tour-class-${idx}-sport`}
                  className="pp-input w-full"
                >
                  <option value="">Inherit (primary)</option>
                  {sports.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Gender" icon={<Users size={10} />}>
              <select
                value={cl.gender}
                onChange={(e) => update(idx, { gender: e.target.value })}
                data-testid={`tour-class-${idx}-gender`}
                className="pp-input w-full"
              >
                {GENDERS.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
              </select>
            </Field>
            <Field label="Format">
              <select
                value={cl.format}
                onChange={(e) => update(idx, { format: e.target.value })}
                data-testid={`tour-class-${idx}-format`}
                className="pp-input w-full"
              >
                {FORMATS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
              </select>
            </Field>
            <Field label="Capacity">
              <input
                type="number" min={2} max={256}
                value={cl.capacity}
                onChange={(e) => update(idx, { capacity: Number(e.target.value || 0) })}
                data-testid={`tour-class-${idx}-capacity`}
                className="pp-input w-full"
              />
            </Field>
            <Field label="Age min" icon={<Calendar size={10} />}>
              <input
                type="number" min={0} max={120}
                value={cl.age_min ?? ""}
                onChange={(e) => update(idx, { age_min: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="—"
                data-testid={`tour-class-${idx}-age-min`}
                className="pp-input w-full"
              />
            </Field>
            <Field label="Age max">
              <input
                type="number" min={0} max={120}
                value={cl.age_max ?? ""}
                onChange={(e) => update(idx, { age_max: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="—"
                data-testid={`tour-class-${idx}-age-max`}
                className="pp-input w-full"
              />
            </Field>
            <Field label="Rating system" icon={<Award size={10} />}>
              <input
                type="text"
                value={cl.skill_rating_system || ""}
                onChange={(e) => update(idx, { skill_rating_system: e.target.value })}
                placeholder="DUPR / UTR / FIDE / …"
                data-testid={`tour-class-${idx}-rating-sys`}
                className="pp-input w-full"
              />
            </Field>
            <Field label="Rating min">
              <input
                type="number" step={0.1} min={0}
                value={cl.skill_rating_min ?? ""}
                onChange={(e) => update(idx, { skill_rating_min: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="—"
                data-testid={`tour-class-${idx}-rating-min`}
                className="pp-input w-full"
              />
            </Field>
            <Field label="Rating max">
              <input
                type="number" step={0.1} min={0}
                value={cl.skill_rating_max ?? ""}
                onChange={(e) => update(idx, { skill_rating_max: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="—"
                data-testid={`tour-class-${idx}-rating-max`}
                className="pp-input w-full"
              />
            </Field>
            <Field label="Price">
              <div className="flex gap-1">
                <input
                  type="number" step={0.5} min={0}
                  value={cl.price ?? 0}
                  onChange={(e) => update(idx, { price: Number(e.target.value || 0) })}
                  data-testid={`tour-class-${idx}-price`}
                  className="pp-input flex-1"
                />
                <select
                  value={cl.currency || "EUR"}
                  onChange={(e) => update(idx, { currency: e.target.value })}
                  data-testid={`tour-class-${idx}-currency`}
                  className="pp-input w-16"
                >
                  {["EUR", "USD", "GBP", "CAD", "AUD", "MXN", "BRL", "JPY"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </Field>
          </div>

          <Field label="Notes">
            <input
              type="text"
              value={cl.notes || ""}
              onChange={(e) => update(idx, { notes: e.target.value })}
              placeholder="Prizes, format details, restrictions…"
              data-testid={`tour-class-${idx}-notes`}
              className="pp-input w-full"
            />
          </Field>
        </div>
      ))}
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1 mb-1">
        {icon}{label}
      </label>
      {children}
    </div>
  );
}
